/**
 * State hook for the chats viewer: react-query reads over the viewer api leaf. Conversations load ONE page at a time,
 * threads and participants only when opened.
 *
 * WHY REACT-QUERY AND NOT THE HAND-ROLLED STACK IT REPLACED. This hook used to hold three `useState` triples of
 * `{ items, loading, error }` driven from `.then(...)/.catch(...)` chains -- a cache of ONE per concept, discarded on
 * every re-select: stepping back to a page already read re-issued its request, reopening a conversation re-fetched its
 * whole thread, and an in-flight read whose selection had already moved on still landed in state, because nothing was
 * cancellable. `createMFEMount` (`components/common/create-mfe-mount.tsx`) already wraps EVERY chats mount in a
 * `QueryClientProvider` carrying `DEFAULT_QUERY_OPTIONS`, so the cache this surface was hand-rolling around was present
 * the whole time and simply unused. Nothing about the wire changed: same four alias routes, same shell-bridge Firebase
 * credential, same worker-side paging clamp.
 *
 * THIS CACHE IS THIS MFE'S OWN AND IS NOT SHARED WITH `spa/connect`, which reads the same three `chat-viewer-*` flows.
 * That is not a gap left for later. The two are separately-built bundles with their own module graphs, so each resolves
 * its own `@tanstack/react-query` instance and its own `QueryClient` -- there is no object for them to share even when
 * both are mounted on one page. Nor do they share a transport: connect dispatches BY FLOW ID through `fetchAdminApi`,
 * while this leaf dials the G4-gated alias routes with the bridge token that `api.ts` explains it must not give up. A
 * hook hoisted to `components/` would have to pick one credential and break the other. So the keys are co-located here,
 * exactly as `connectQueryKeys` is co-located in connect's own hooks leaf.
 *
 * THE RETURNED SHAPE IS UNCHANGED (`ListData` / `ThreadData` / `PartData`), which is what leaves `App.tsx` and the three
 * panes below it untouched by the conversion. Every query's FAILURE member is read, through the canonical
 * `readQueryError` rather than a fourth respelling of `instanceof Error` -- the shape
 * `QUAL-FRONTEND-QUERY-ERROR-BLIND-001` names in its own fix.
 */

import { useCallback, useState } from 'react';
import { useFlagSetter } from '@federa/components/hooks/useFlagSetter';
import { useQuery } from '@tanstack/react-query';
import { readQueryError } from '@federa/components/feedback/query-pane-parts';
import { errorMessages } from '@federa/utils/styles/content-tokens';
import { fetchViewerConversationsPage, fetchViewerMessages, fetchViewerParticipants, searchViewerConversations } from './api.ts';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ChatConversationsPage, ChatViewerConversation, ChatViewerMessage, ChatViewerParticipant } from '@federa/types/conversations';
import type { PartData, ThreadData, ListData } from '@federa/types/spa-chats-shapes';

/** The cache ROOT every viewer key hangs under. Named once, so a fifth key cannot land in a namespace of its own and
 *  quietly stop being invalidated with the rest of this surface. */
const CHATS_VIEWER_NAMESPACE = 'chats-viewer';

/** One namespace per viewer concept. The page number and the conversation id ride INSIDE the key rather than beside it,
 *  so stepping back to a page already read -- or reopening a thread -- is a cache hit instead of a request. */
export const chatsViewerQueryKeys = {
	conversationsPage: (page: number) => [CHATS_VIEWER_NAMESPACE, 'conversations', page] as const,
	search: (query: string) => [CHATS_VIEWER_NAMESPACE, 'search', query] as const,
	thread: (conversationId: string) => [CHATS_VIEWER_NAMESPACE, 'thread', conversationId] as const,
	participants: (conversationId: string) => [CHATS_VIEWER_NAMESPACE, 'participants', conversationId] as const,
};

/**
 * The three pane projections are MODULE-LEVEL and PURE, not inline blocks in the hook body.
 *
 * They read no state and close over nothing, so the hook body is left holding only the state and the four query
 * declarations -- which is what keeps it under the `max-lines-per-function` and `complexity` caps that the inline
 * form broke (82 lines / complexity 22 against 80 / 14). Each helper's own branch count is the pane's real decision
 * count, so a reader sees the projection rule for one pane rather than three interleaved ladders.
 */

/** EXACTLY ONE of the two list reads is enabled at a time, so the disabled one's permanently-pending state is never
 *  the one read here -- which is why `isPending` may be surfaced raw on the live arm. */
function readListData(
	at: { page: number; searchQuery: string | null },
	pageRead: UseQueryResult<ChatConversationsPage>,
	searchRead: UseQueryResult<ChatViewerConversation[]>,
): ListData {
	const { page, searchQuery } = at;
	if (searchQuery !== null) {
		return {
			items: searchRead.data ?? [],
			page: 0,
			hasMore: false,
			loading: searchRead.isPending,
			error: readQueryError(searchRead, errorMessages.generic),
			searchQuery,
		};
	}
	return {
		items: pageRead.data?.conversations ?? [],
		page,
		hasMore: pageRead.data?.hasMore ?? false,
		loading: pageRead.isPending,
		error: readQueryError(pageRead, errorMessages.generic),
		searchQuery: null,
	};
}

/** A disabled query reports `isPending` forever, so every member is gated on a conversation actually being selected. */
function readThreadData(conversation: ChatViewerConversation | null, threadRead: UseQueryResult<ChatViewerMessage[]>): ThreadData {
	if (conversation === null) return { conversation: null, messages: [], loading: false, error: null };
	return {
		conversation,
		messages: threadRead.data ?? [],
		loading: threadRead.isPending,
		error: readQueryError(threadRead, errorMessages.generic),
	};
}

/** Same disabled-query rule as the thread, over the panel's own gate: a closed panel must read as inert, not as
 *  perpetually loading. */
function readPartsData(partsOpen: boolean, conversationId: string | null, partsRead: UseQueryResult<ChatViewerParticipant[]>): PartData {
	const live = partsOpen && conversationId !== null;
	if (!live) return { open: partsOpen, items: [], loading: false, error: null };
	return {
		open: partsOpen,
		items: partsRead.data ?? [],
		loading: partsRead.isPending,
		error: readQueryError(partsRead, errorMessages.generic),
	};
}

export function useChatsViewer() {
	const [page, setPage] = useState(0);
	// `null` is the PAGED arm, NOT "an empty search": an emptied box clears back to the list rather than searching for ''.
	const [searchQuery, setSearchQuery] = useState<string | null>(null);
	const [conversation, setConversation] = useState<ChatViewerConversation | null>(null);
	const [partsOpen, setPartsOpen] = useState(false);
	const conversationId = conversation?.id ?? null;

	const pageRead = useQuery({
		queryKey: chatsViewerQueryKeys.conversationsPage(page),
		enabled: searchQuery === null,
		queryFn: ({ signal }) => fetchViewerConversationsPage(page, signal),
	});

	const searchRead = useQuery({
		queryKey: chatsViewerQueryKeys.search(searchQuery ?? ''),
		enabled: searchQuery !== null,
		queryFn: ({ signal }) => searchViewerConversations(searchQuery ?? '', signal),
	});

	const threadRead = useQuery({
		queryKey: chatsViewerQueryKeys.thread(conversationId ?? ''),
		enabled: conversationId !== null,
		queryFn: ({ signal }) => fetchViewerMessages(conversationId ?? '', signal),
	});

	const partsRead = useQuery({
		queryKey: chatsViewerQueryKeys.participants(conversationId ?? ''),
		enabled: partsOpen && conversationId !== null,
		queryFn: ({ signal }) => fetchViewerParticipants(conversationId ?? '', signal),
	});

	const list = readListData({ page, searchQuery }, pageRead, searchRead);
	const thread = readThreadData(conversation, threadRead);
	const parts = readPartsData(partsOpen, conversationId, partsRead);

	const loadPage = useCallback((next: number) => {
		setSearchQuery(null);
		setPage(next);
	}, []);

	/** Run a username/phone search on demand (button click). Empty query clears back to the paged list. */
	const search = useCallback(
		(query: string) => {
			const q = query.trim();
			if (!q) {
				loadPage(0);
				return;
			}
			setSearchQuery(q);
		},
		[loadPage],
	);

	const openConversation = useCallback((next: ChatViewerConversation) => {
		setPartsOpen(false);
		setConversation(next);
	}, []);

	// The no-selection guard is KEPT from the hand-rolled predecessor: `parts.open` drives whether the slide-over is in
	// the tree at all, so opening it with nothing selected would render an empty panel over a viewer that had never been
	// asked for one.
	const showParticipants = useCallback(() => {
		if (conversationId !== null) setPartsOpen(true);
	}, [conversationId]);
	const closeParticipants = useFlagSetter(setPartsOpen, false);

	return { list, thread, parts, loadPage, search, openConversation, showParticipants, closeParticipants };
}
